import {
  ThreadsWithMeetingNoteDocument,
  ThreadsWithMeetingNoteSubscription,
  ThreadsWithMeetingNoteSubscriptionVariables,
  useUpdateThreadActivityMutation,
} from '@gql'
import { useApolloClient } from '@apollo/client'
import { useMemo } from 'react'
import { createMeetingNoteSaver } from '../utils/meetingNoteSaver'
import useCreateThreadMeetingNote from './useCreateThreadMeetingNote'

// Saves the meeting notes of a tension (#197): creates the note once, then
// updates it, even before the subscription has returned it.
export default function useSaveThreadMeetingNote(
  threadId: string,
  meetingId: string | undefined
) {
  const client = useApolloClient()
  const createThreadMeetingNote = useCreateThreadMeetingNote()
  const [updateThreadActivity] = useUpdateThreadActivityMutation()

  return useMemo(() => {
    if (!meetingId) return async () => {}
    return createMeetingNoteSaver({
      create: (notes) => createThreadMeetingNote(threadId, meetingId, notes),
      update: async (id, notes) => {
        await updateThreadActivity({
          variables: { id, values: { data: { notes } } },
        })
      },
      // One-off read of the active note, through the subscription the meeting
      // already uses (a dedicated query would need a codegen)
      findActive: () =>
        new Promise((resolve, reject) => {
          const subscription = client
            .subscribe<
              ThreadsWithMeetingNoteSubscription,
              ThreadsWithMeetingNoteSubscriptionVariables
            >({
              query: ThreadsWithMeetingNoteDocument,
              variables: { threadsIds: [threadId], meetingId },
              fetchPolicy: 'no-cache',
            })
            .subscribe({
              next: ({ data }) => {
                setTimeout(() => subscription.unsubscribe())
                resolve(data?.thread[0]?.activities[0]?.id)
              },
              error: reject,
            })
        }),
    })
  }, [threadId, meetingId, client, createThreadMeetingNote])
}
